import { useState } from 'react'
import { Alert, App, Button, Card, Table, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { EmptyState } from '@/shared/ui/EmptyState'
import { OperationResultAlert } from '@/shared/ui/OperationResultAlert'
import { RowActions } from '@/shared/ui/table/RowActions'
import { useOperationRunner } from '@/shared/ui/useOperationRunner'
import { activeTimeColumns, networkColumns, trafficColumns } from './SessionColumns'
import { disconnectSession, fetchActiveSessions, type ActiveSession } from './sessionApi'

/**
 * Butun tizim bo'yicha faol sessiyalar.
 *
 * ⚠️ SERVER TOMONDA SAHIFALASH YO'Q: `GET /api/sessions/active` faqat `limit`
 * qabul qiladi (`page` yo'q). Shuning uchun bu jadval `DataTable` ni
 * ISHLATMAYDI — u server sahifalashini talab qiladi va shartni bajara
 * olmaganda yolg'on sahifalash ko'rsatardi. Buning o'rniga chegara ochiq
 * aytiladi: "eng ko'pi bilan N ta ko'rsatilmoqda".
 */
/**
 * ⚠️ EKSPORT QILINADI: chegarani bilishi kerak bo'lgan ikkinchi joy bor —
 * `SessionsPage` dagi KPI. U butun tenant bo'yicha sanaydi va shu jadval
 * ustida turadi, ya'ni «340» yozib, pastda 200 qator ko'rsatardi. Raqamni
 * u yerda takrorlash ikki joyni ajratib yuborardi.
 */
export const ACTIVE_LIMIT = 200

export function ActiveSessionsPanel() {
  const { t } = useTranslation()
  // ⚠️ BUG-048 — `modal` KONTEKSTDAN: statik `Modal.confirm` React
  // daraxtidan tashqarida yangi ildiz ochadi va `ConfigProvider`
  // mavzusiga yetmaydi — qorong'i panel ustida OQ dialog chiziladi.
  const { modal } = App.useApp()
  const runner = useOperationRunner([queryKeys.sessions.active, queryKeys.sessions.onlineLogins])
  /*
    ⚠️ Sahifalash BOSHQARILADIGAN bo'ldi: tartib raqami sahifaga mos
    bo'lishi kerak (2-sahifada 26, 27, …), antd esa boshqarilmagan
    rejimda joriy sahifani tashqariga bermaydi.
  */
  const [page, setPage] = useState(1)

  const query = useQuery({
    queryKey: queryKeys.sessions.active,
    queryFn: () => fetchActiveSessions(ACTIVE_LIMIT),
    // Faol sessiyalar real vaqtda o'zgaradi (07-FRONTEND.md: 10-30 s polling)
  // ⚠️ Avtoyangilanish OLIB TASHLANDI (egasi qarori 2026-08-20):
  //    u faqat bosh sahifada qoladi. Qolgan ekranlarda u backendga
  //    doimiy yuk berardi, holbuki operator sahifani ochiq qoldirib
  //    boshqa ish bilan band bo'ladi. Yangilash QO'LDA — tugma bor.
  })

  const rows = query.data ?? []

  const PAGE_SIZE = 25

  const columns: ColumnsType<ActiveSession> = [
    {
      key: '__rowNo',
      title: t('dataTable.rowNo'),
      width: 56,
      align: 'center',
      fixed: 'left',
      render: (_v, _r, index) => (
        <Typography.Text type="secondary">{(page - 1) * PAGE_SIZE + index + 1}</Typography.Text>
      ),
    },
    {
      key: 'username',
      title: t('session.subscriber'),
      width: 170,
      fixed: 'left',
      render: (_, row) => (
        // Loginni bosib abonent kartochkasiga o'tish — operator odatda
        // sessiyani ko'rgach abonentga qaraydi.
        //
        // ⚠️ QIDIRUV realmsiz login bilan: `row.username` `radacct` dan
        // keladi va u `room101@uz-venue` shaklida. Abonentlar ro'yxatida
        // esa toza `room101` turadi — realm bilan qidirilsa HECH NARSA
        // topilmasdi va havola o'lik bo'lardi.
        //
        // Ko'rsatiladigan matn esa TO'LIQ qoladi: operator aynan shu
        // nomni abonentga beradi.
        <Link to={`/subscribers?q=${encodeURIComponent(row.username)}`}>
          <Typography.Text strong>{row.username}</Typography.Text>
        </Link>
      ),
    },
    ...networkColumns<ActiveSession>(t),
    ...activeTimeColumns(t),
    ...trafficColumns<ActiveSession>(t),
    {
      key: 'actions',
      title: '',
      width: 56,
      align: 'center',
      fixed: 'right',
      render: (_, row) => (
        <RowActions
          items={[
            {
              key: 'disconnect',
              label: t('actions.disconnect'),
              permission: 'subscriber.session.disconnect',
              danger: true,
              onClick: () => {
                modal.confirm({
                  title: t('actions.disconnectSessionTitle'),
                  // ⚠️ ARGUMENTLAR MAJBURIY. Matnda `{{username}}` va `{{ip}}`
                  // o'rinlari bor; ular berilmasa i18next ularni BO'SH SATRGA
                  // almashtiradi va tasdiqlash oynasi " abonentining sessiyasi
                  // () uziladi" bo'lib chiqardi — ya'ni operator AYNAN QAYSI
                  // sessiyani uzayotganini ko'rmasdan "Ha" bosardi.
                  content: t('actions.disconnectSessionConfirm', {
                    username: row.username,
                    ip: row.framedIp ?? '—',
                  }),
                  okButtonProps: { danger: true },
                  okText: t('actions.disconnect'),
                  cancelText: t('common.cancel'),
                  onOk: () => runner.run(() => disconnectSession(row.radacctId)),
                })
              },
            },
          ]}
        />
      ),
    },
  ]

  return (
    <Card
      title={t('dashboard.activeSessions')}
      extra={
        <Typography.Text type="secondary">
          {t('sessions.limitNote', { limit: ACTIVE_LIMIT })}
        </Typography.Text>
      }
    >
      {runner.result ? (
        <div style={{ marginBottom: 16 }}>
          <OperationResultAlert result={runner.result} closable onClose={runner.clear} />
        </div>
      ) : null}

      {/*
        ⚠️ XATO HOLATI — bu tekshiruvsiz panel YOLG'ON GAPIRADI.
        So'rov yiqilsa `rows` bo'sh qoladi va operator toza, ishonchli
        ko'rinishdagi bo'sh jadvalni ko'radi: "hech kim onlayn emas".
        Bu mahsulotdagi eng qimmat yolg'on — operator hech narsa
        qilmaydi, chunki muammo yo'qdek ko'rinadi.
      */}
      {query.isError ? (
        <Alert
          type="error"
          showIcon
          message={t('dataTable.loadFailed')}
          description={translateError(query.error, t)}
          action={
            <Button size="small" onClick={() => void query.refetch()}>
              {t('dataTable.retry')}
            </Button>
          }
        />
      ) : (
        <Table<ActiveSession>
          columns={columns}
          dataSource={rows}
          rowKey={(row) => row.radacctId}
          loading={query.isPending}
          size="small"
          scroll={{ x: 1200 }}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            showSizeChanger: false,
            onChange: (nextPage) => setPage(nextPage),
          }}
          // ⚠️ "Ma'lumot yo'q" emas, ANIQ xabar. Umumiy matn
          // "yuklanmadi" bilan "hech kim yo'q" ni ajratmaydi.
          locale={{ emptyText: <EmptyState title={t('dashboard.noActiveSessions')} /> }}
        />
      )}
    </Card>
  )
}
